import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CalendarDays, Clock, MapPin, Megaphone, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import api, { fileUrl } from "@/lib/api";
import { Reveal, SectionHeading, EmptyState, formatDate } from "@/components/public/Shared";
import BirthdayBanner from "@/components/public/BirthdayBanner";

export default function Home() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/public/home").then((r) => setData(r.data)).catch(() => setData({ settings: {} }));
  }, []);

  const s = data?.settings || {};
  const hero = s.hero_image_url || "https://images.unsplash.com/photo-1553022488-cd376fe13e4a?q=80&w=2000&auto=format&fit=crop";

  return (
    <div data-testid="home-page">
      <BirthdayBanner />
      <section className="relative min-h-[88vh] flex items-center overflow-hidden bg-navy">
        <img src={hero} alt="Christ Church CNI Rajpura church building" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-navy via-navy/80 to-navy/30" />
        <div className="absolute inset-0 grain-overlay" />
        <div className="relative mx-auto w-full max-w-7xl px-5 py-24 sm:px-8">
          <div className="max-w-2xl animate-fade-up">
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-gold mb-5">
              Church of North India · Chandigarh Diocese
            </p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-cream leading-[1.05] text-balance">
              {s.church_name || "Christ Church CNI, Rajpura"}
            </h1>
            <p className="mt-6 max-w-xl text-base md:text-lg leading-relaxed text-cream/85">
              {s.tagline || "A warm and welcoming family of faith in Rajpura."} You are warmly invited to worship, fellowship and grow with us.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Button asChild size="lg" className="bg-gold text-navy hover:bg-gold-dark hover:text-cream transition-colors" data-testid="hero-about-btn">
                <Link to="/about">About Our Church <ArrowRight className="ml-2 h-4 w-4" /></Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-cream/40 bg-transparent text-cream hover:bg-cream/10 hover:text-cream" data-testid="hero-events-btn">
                <Link to="/events">Upcoming Events</Link>
              </Button>
              <Button asChild size="lg" variant="ghost" className="text-cream/90 hover:text-gold hover:bg-transparent" data-testid="hero-contact-btn">
                <Link to="/contact">Contact Us</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-20 sm:px-8 sm:py-28">
        <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
          <Reveal>
            <SectionHeading
              kicker="Welcome"
              title={`Welcome to ${s.church_name || "Christ Church CNI, Rajpura"}`}
              text={s.welcome_text || ""}
            />
            <Button asChild variant="link" className="mt-4 px-0 text-gold-dark" data-testid="welcome-more-btn">
              <Link to="/about">Learn more about us <ArrowRight className="ml-1 h-4 w-4" /></Link>
            </Button>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="rounded-lg border border-border bg-white p-8 shadow-sm">
              <p className="text-xs font-semibold uppercase tracking-[0.25em] text-gold-dark mb-5">Worship With Us</p>
              <div className="space-y-4">
                {(data?.services || []).slice(0, 3).map((svc) => (
                  <div key={svc.id} className="flex items-start gap-4 border-b border-border pb-4 last:border-0 last:pb-0">
                    <Clock className="h-5 w-5 mt-0.5 shrink-0 text-gold" />
                    <div>
                      <p className="font-semibold text-navy">{svc.name}</p>
                      <p className="text-sm text-muted-foreground">{svc.day} · {svc.time}</p>
                    </div>
                  </div>
                ))}
                {data && (data.services || []).length === 0 && (
                  <p className="text-sm text-muted-foreground">{s.service_note || "Service timings will be updated soon."}</p>
                )}
              </div>
              <Button asChild variant="outline" className="mt-6 w-full border-navy/20 text-navy hover:bg-secondary" data-testid="all-services-btn">
                <Link to="/services">All Worship & Services</Link>
              </Button>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-secondary/60">
        <div className="mx-auto max-w-7xl px-5 py-20 sm:px-8">
          <Reveal>
            <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
              <SectionHeading kicker="Gather Together" title="Upcoming Events" />
              <Button asChild variant="outline" className="border-navy/20 text-navy" data-testid="view-events-btn">
                <Link to="/events">View all events</Link>
              </Button>
            </div>
          </Reveal>
          {data && (data.events || []).length === 0 && (
            <EmptyState testId="home-events-empty" title="No upcoming events yet" text="New church events will be announced here. Please check back soon." />
          )}
          <div className="grid gap-6 md:grid-cols-3">
            {(data?.events || []).map((ev, i) => (
              <Reveal key={ev.id} delay={i * 0.1}>
                <article className="group h-full rounded-lg border border-border bg-white p-7 shadow-sm transition-transform duration-300 hover:-translate-y-1" data-testid={`home-event-${ev.id}`}>
                  <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-gold-dark">
                    <CalendarDays className="h-4 w-4" /> {formatDate(ev.date)}
                  </p>
                  <h3 className="mt-3 font-heading text-xl font-bold text-navy">{ev.title}</h3>
                  <p className="mt-2 text-sm text-muted-foreground line-clamp-3">{ev.description}</p>
                  {ev.location && <p className="mt-4 flex items-center gap-1.5 text-xs text-muted-foreground"><MapPin className="h-3.5 w-3.5" />{ev.location}</p>}
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-20 sm:px-8">
        <Reveal>
          <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
            <SectionHeading kicker="Stay Informed" title="Latest Announcements" />
            <Button asChild variant="outline" className="border-navy/20 text-navy" data-testid="view-announcements-btn">
              <Link to="/announcements">All announcements</Link>
            </Button>
          </div>
        </Reveal>
        <div className="grid gap-6 md:grid-cols-3">
          {(data?.announcements || []).map((a, i) => (
            <Reveal key={a.id} delay={i * 0.1}>
              <article className="h-full rounded-lg border-l-4 border-gold bg-white p-7 shadow-sm" data-testid={`home-announcement-${a.id}`}>
                <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                  <Megaphone className="h-4 w-4 text-gold" /> {formatDate(a.date)} {a.category ? `· ${a.category}` : ""}
                </p>
                <h3 className="mt-3 font-heading text-xl font-bold text-navy">{a.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground line-clamp-4 whitespace-pre-line">{a.body}</p>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      {(data?.photos || []).length > 0 && (
        <section className="bg-navy grain-overlay">
          <div className="mx-auto max-w-7xl px-5 py-20 sm:px-8">
            <Reveal>
              <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.25em] text-gold mb-3">Our Life Together</p>
                  <h2 className="font-heading text-3xl sm:text-4xl font-bold text-cream">From Our Photo Gallery</h2>
                </div>
                <Button asChild variant="outline" className="border-cream/30 bg-transparent text-cream hover:bg-cream/10" data-testid="view-gallery-btn">
                  <Link to="/gallery">View gallery</Link>
                </Button>
              </div>
            </Reveal>
            <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
              {data.photos.map((p, i) => (
                <Reveal key={p.id} delay={i * 0.06}>
                  <figure className="overflow-hidden rounded-lg ring-1 ring-cream/10">
                    <img src={fileUrl(p.image_url)} alt={p.title || "Church photograph"} className="aspect-[4/3] w-full object-cover transition-transform duration-500 hover:scale-105" loading="lazy" />
                  </figure>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {(data?.ministries || []).length > 0 && (
        <section className="mx-auto max-w-7xl px-5 py-20 sm:px-8">
          <Reveal>
            <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
              <SectionHeading kicker="Serve & Grow" title="Our Ministries" />
              <Button asChild variant="outline" className="border-navy/20 text-navy" data-testid="view-ministries-btn">
                <Link to="/ministries">Explore ministries</Link>
              </Button>
            </div>
          </Reveal>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {data.ministries.map((m, i) => (
              <Reveal key={m.id} delay={i * 0.08}>
                <div className="h-full rounded-lg border border-border bg-white p-7 shadow-sm transition-transform duration-300 hover:-translate-y-1" data-testid={`home-ministry-${m.id}`}>
                  <Users className="h-6 w-6 text-gold" />
                  <h3 className="mt-4 font-heading text-xl font-bold text-navy">{m.name}</h3>
                  <p className="mt-2 text-sm text-muted-foreground line-clamp-3">{m.description}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      <section className="bg-secondary/60">
        <div className="mx-auto max-w-7xl px-5 py-20 sm:px-8 text-center">
          <Reveal>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-navy text-balance">Plan Your Visit</h2>
            <p className="mx-auto mt-4 max-w-xl text-base text-muted-foreground">
              We would love to welcome you and your family. Reach out to us or join us for worship this week.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <Button asChild size="lg" className="bg-navy text-cream hover:bg-navy-light" data-testid="visit-contact-btn">
                <Link to="/contact">Contact Us</Link>
              </Button>
              <Button asChild size="lg" className="bg-gold text-navy hover:bg-gold-dark hover:text-cream" data-testid="visit-services-btn">
                <Link to="/services">Worship Timings</Link>
              </Button>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
